import { observer } from 'mobx-react-lite';
import { Document, Page } from '@react-pdf/renderer';
import Html from 'react-pdf-html';
import ReactDOMServer from 'react-dom/server';
import { useStore } from '../../../app/stores/store';
import { useEffect } from 'react';
import { initialOption, metadataType } from '../../../app/api/types';
import {
  label,
  pictureVendor,
  summaryIndex,
  QualityMonitorColorist,
  NFHMQCSupervisorNameandSignature,
  GradingQCCheckingDate,
  ColorGradingHeaders,
  tempElementDetails,
} from './color-grading-constants';
import { getImageSrc } from '../../../utils/ImageHelper';

interface DynamicTableData {
  headers: string[];
  body: any[];
}

interface LocalMetaData {
  [key: number]: metadataType[];
}


export const getColumn = (data: metadataType[], num: number) => {
  const halfLength = Math.ceil(data?.length / 2);
  return num === 1 ? data?.slice(0, halfLength) : data?.slice(halfLength);
};
export const CheckboxHandler = (
  options: any[],
  value: string,
  name?: any,
  ReportName?: string
): JSX.Element => {

  const values = value?.split('|') || []; // Split value into array, default to empty array if undefined

  const html = options?.map((option) => {
    const imageUrl = values?.includes(option.optionName) ? 'CheckedCheckbox' : 'Checkbox';
    return {
      imageUrl,
      optionName: option.optionName,
      optionIndex: option.optionIndex
    };
  });

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: name === 'left' || ReportName === 'RealAnalysisReport' ? 'left' : name === 'right' ? 'right' : 'center',
      }}
    >
      {html
      ?.sort((a, b) => a.optionIndex - b.optionIndex) // Sorting by optionIndex
      ?.map((item, index) => (
        <span
          key={index}
          style={
            name === 'Film Element'
              ? { marginRight: '5px', justifyContent: 'center' }
              : {
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  marginLeft: '2px',
                }
          }
        >
          <img
            src={item.imageUrl === 'CheckedCheckbox' 
              ? '../assets/NFAI_Restoration_Checkbox_Checked.png' 
              : '../assets/NFAI_Restoration_Checkbox_Unchecked.png'}
            alt={item.optionName}
            style={
              ReportName === 'RestorationReport'
                ? {
                  width: '10px',
                  height: '10px',
                  marginRight: '2px',
                  marginLeft: item.optionIndex === 0 ? '-3px' : '0px'
                }
                : name === 'DM'
                ? { width: '9px', height: '9px', marginRight: '3px',marginTop:'-1px' }
                : { width: '10px', height: '10px', marginRight: '2px', marginLeft: item.optionIndex === 0 ? '-3px' : '0px' }
            }
          />
          <span style={{ fontSize: '14px', paddingTop: '1px' }}>
            {name === 'Bit Depth' ? `${item.optionName} Bit` : item.optionName}
          </span>
          {(ReportName === 'RestorationReport' || ReportName === 'RealAnalysisReport') && <br />}
        </span>
      ))}
    </div>
  );
};

export const dateTimeStamp = (value: any): string => {
  let date: Date;
  if (value === undefined || value === '') {
    return '';
  } else {
    date = new Date(value);
  }
  const year = date.getFullYear() % 100;
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const formattedYear = year.toString().padStart(2, '0');
  const formattedMonth = month.toString().padStart(2, '0');
  const formattedDay = day.toString().padStart(2, '0');
  const formattedDateString = `${formattedDay}/${formattedMonth.slice(0,3)}/${formattedYear}`;
  return formattedDateString;
};

export const dateConvertHandler = (date: string,type?: string) => {
  if (date === undefined || date.includes('undefined') || date === '') {
    const dateObj = new Date();
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();
    return `${day}-${month}-${year}`;
  }

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  let day: string = '';
  let month: string = '';
  let year: string = '';

  if (date.includes('(India Standard Time)')) {
    const dateObj = new Date(date);
    day = String(dateObj.getDate());
    month = String(dateObj.getMonth() + 1); // Month is zero-based, so we add 1
    year = String(dateObj.getFullYear());
  } else {
    [day, month, year] = date.split('/');
  }
const months = monthNames[parseInt(month) - 1 || 0].slice(0,3);
  return type === 'typeFirst'
    ? `${day}. ${months} ${year}`
    : `${day}-${months}-${year}`;
}

const optionNames = ['YES', 'NO'];

const initialOptions:any[] = optionNames.map((name, index) => ({
  ...initialOption,
  optionName: name,
  optionIndex: index + 1, // index starts at 0, so +1 to make it 1 and 2
}));
const YesOrNoResponse = (input:string) => {
  const hasYes = input.toUpperCase().includes("YES");
  const hasNo = input.toUpperCase().includes("NO");

  if (hasYes && hasNo) {
    return "YES|NO";
  } else if (hasYes) {
    return "YES";
  } else if (hasNo) {
    return "NO";
  } else {
    return ""; // or handle invalid case
  }
}

const extractDrpFileName = (text: string): string => {
  // Remove leading "YES", "NO", "YES and NO", etc., and return the rest trimmed
  return text
    ?.replace(/^YES\/NO/i, '')
    .replace(/^YES/i, '')
    .replace(/^NO/i, '')
    .trim() || '';
};


function ColorGradingReport({isDualPDF}:{isDualPDF:boolean}) {
  const {
    viewStore: { metadata, parentMetadata },
    activityStore: { selectedActivity },
    masteringStore
  } = useStore();

  useEffect(() => {
    if (selectedActivity?.id) {
      parentMetadata({ itemId: selectedActivity?.id });
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const tempTableData: LocalMetaData = {
      0: [],
      1: [],
      2: [],
      3: [],
    };
    metadata?.forEach((m: metadataType) => {
      if (
        pictureVendor.includes(m.elementName) &&
        !m.elementName.includes('Project Id/Code')
      ) {
        tempTableData[0].push(m);
      }
      if(m.elementName === 'Film Scanning Type'){
        masteringStore.ColorGradingData.filmScannedType = [m];
      }
      if (
        m.elementType === 'DynamicTable' &&
        m.elementName === 'Colour Grading'
      ) {
        getDynamicTableData(m.elementValue, 'Colour Grading', m.options);
      }

      if (QualityMonitorColorist.includes(m.elementName)) {
        tempTableData[1].push(m);
      }
      if (
        m.elementType === 'DynamicTable' &&
        m.elementName === 'NFHM Grading QC Supervisor Feedback'
      ) {
        getDynamicTableData(
          m.elementValue,
          'NFHM Grading QC Supervisor Feedback',
          m.options,
        );
      }
      if (summaryIndex.includes(m.elementName)) {
        masteringStore.ColorGradingData.summary = m.elementValue;
      }
      if (NFHMQCSupervisorNameandSignature.includes(m.elementName)) {
        tempTableData[2].push(m);
      }

      if (GradingQCCheckingDate.includes(m.elementName)) {
        tempTableData[3].push(m);
      }
    });

    tempTableData[0] = sortArrayByOrder(tempTableData[0], pictureVendor);
    const lastData = tempTableData[0].splice(-1)[0]
    tempTableData[0].push(tempElementDetails,tempElementDetails,tempElementDetails,lastData)
    tempTableData[1] = sortArrayByOrder(
      tempTableData[1],
      QualityMonitorColorist,
    );
    masteringStore.ColorGradingData.localMetaData = tempTableData;
    // setLocalMetaData(tempTableData);
     // eslint-disable-next-line
  }, [metadata]);

  function sortArrayByOrder(inputArray: any[], sortingOrder: string[]) {
    const elementIndexMap: Record<string, number> = {};
    sortingOrder.forEach((element, index) => {
      elementIndexMap[element] = index;
    });

    return inputArray.slice().sort((a, b) => {
      const aIndex = elementIndexMap[a.elementName];
      const bIndex = elementIndexMap[b.elementName];
      return aIndex - bIndex;
    });
  }

  const getDynamicTableData = (
    eleVal: string,
    value: string,
    header: any[],
  ) => {
    const temp: DynamicTableData = {
      headers: [],
      body: [],
    };

    let columnHeaders: string[] = [];
    let tempValue: any[] = [];

    if (eleVal?.includes('DynamicTable|')) {
      let stringData = eleVal?.split('DynamicTable|')?.[1]?.replaceAll(/\n/g,'');
      stringData = stringData?.split('_')?.join(' ');
      tempValue = stringData !== undefined ? JSON.parse(stringData) : [];
      columnHeaders = Object.keys(tempValue?.[0] || []);
      columnHeaders = columnHeaders?.filter((data) => data !== ' uuid ');
    }

    temp.headers = columnHeaders;
    temp.body = tempValue;

    if (value === 'Colour Grading') {
      temp.headers = temp.headers
        ?.filter((item: any) => item !== 'Warning Pass Fail')
        .sort(
          (a, b) =>
            ColorGradingHeaders.indexOf(a) - ColorGradingHeaders.indexOf(b),
        );
      temp.body.forEach(function (item) {
        // eslint-disable-next-line
        item['Check Parameters'] = item['Check Parameters'];
        // eslint-disable-next-line
        item['Technical Description'] = item['Technical Description'];
        // eslint-disable-next-line
        item['Warning / Pass  / Fail'] = item['Warning / Pass  / Fail'];
        // eslint-disable-next-line
        item['Remarks'] = item['Remarks'];
        delete item[' uuid '];
        delete item['Warning Pass Fail'];
      });
      masteringStore.ColorGradingData.dynData = temp;
    } else {
      var desiredOrder = [
        "Reel no.",
        "Time-code",
        "Grading details/ issues",
        "Rectified by  Vendor (Yes/ No)",
        "Approved/ Not Approved"
      ]
      temp.headers = header.filter((item) => {
        return item.optionName !== 'Start Frame' && item.optionName !== 'End Frame';
      }).map((item) => {
        return item.optionName;
      });
      temp.headers = desiredOrder.map((item) => {
        return temp.headers.find((element) => element === item) || ""; // Find the item in the original array and return it, or return an empty string if not found
      });
      masteringStore.ColorGradingData.dynDataOne = temp;
    }
  };



  const element = (
    <html>
      <body>
        <style>
          {`
          * {
            font-size: 7px;
          }
          #container {
            margin: 5px 25px;
          }
          #container tbody {
            column-count: 2;
            -moz-column-count: 2;
            -webkit-column-count: 2;
          }
          .l-grey-bg {
            background-color: lightgray;
          }
          .grey-bg {
            background-color: gray;
          }
          .CGR-table {
            width: 90%;
            margin-left: auto;
            margin-right: auto;
          }
          .center-content {
            text-align: center;
          }
          .padding-4 {
            padding: 4px;
          }
          .padding-2 {
            padding: 2px;
          }
          .CGR-table,
          .CGR-table th,
          .CGR-table td {
            border: 1.2px solid black;
            border-collapse: collapse;
          }
          .CGR-table td {
            padding: 3px;
          }
          .CGR-table table,
          .CGR-table table th,
          .CGR-table table td {
            border: none;
            border-collapse: collapse;
          }
          .bold {
            font-weight: 700;
          }
          .border-top-0 {
            border-top: 0px;
          }
          .white-text {
            color: white;
          }
          .CGR-table .bold-extra {
            font-weight: 900 !important;
          }
          .d-grey-bg {
          background-color: gray;
        }
         
          
          
          `}
        </style>
        <div id="container">
          <table className="CGR-table">
            <thead>
              <tr>
                <th className="center-content padding-4 bold">
                  <strong> COLOUR GRADING - QUALITY CHECK REPORT</strong>
                </th>
              </tr>
              <tr>
                <th className="l-grey-bg center-content padding-2 bold">
                  NFHM Restoration Project
                </th>
              </tr>
              <tr></tr>
              <tr></tr>
            </thead>
            <tbody>
              <tr>
                <td className="center-content">PICTURE VENDOR NAME:</td>
                <td className="center-content">
                  Prasad Corporation Private Limited, Chennai
                </td>
              </tr>
                          {getColumn(masteringStore.ColorGradingData.localMetaData[0], 1)?.map((l, index) => {
                  const column2Item = getColumn(masteringStore.ColorGradingData.localMetaData[0], 2)?.[index];

                  // Special Case: Film Type with Film Scanning Type
                  if (l.elementName === 'Film Type') {
                    return (
                      <tr key={l.elementIndex}>
                        <td style={{ display: 'flex', flexDirection: 'row' }}>
                          {(label[l.elementName as keyof typeof label] || l.elementName)?.toUpperCase()} :
                          {' '}
                          {CheckboxHandler(l?.options, l?.elementValue, 'DM')}
                        </td>
                        <td className="center-content" colSpan={3} style={{ display: 'flex', flexDirection: 'row' }}>
                          {masteringStore.ColorGradingData.filmScannedType?.map((element) => (
                              <>
                                {(label[element.elementName as keyof typeof label] || element.elementName)?.toUpperCase()} :
                                {' '}
                                {CheckboxHandler(element?.options, element?.elementValue, 'DM')}
                              </>
                            ))}
                        </td>
                      </tr>
                    );
                  }

                  // Case: Film Element or Film Gauge
                  if (['Film Element', 'Film Gauge'].includes(l.elementName)) {
                    return (
                      <tr key={l.elementIndex}>
                        <td>
                          {(label[l.elementName as keyof typeof label] || l.elementName)?.toUpperCase()}:
                        </td>
                        <td className="center-content" colSpan={3}>
                          {l?.options?.length !== 0 && l.elementName !== 'Film Gauge' ? (
                            CheckboxHandler(l?.options, l?.elementValue, l?.elementName)
                          ) : (<>{l?.elementValue}</>
                            // <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
                            //   {l?.elementValue?.split('|')?.map((value, idx) => (
                            //     <div key={idx} style={{ display: 'flex', margin: '0 4px' }}>
                            //       <img
                            //         src="../assets/NFAI_Restoration_Checkbox_Checked.png"
                            //         alt={`Checkbox for ${value}`}
                            //         style={{ width: '10px', height: '10px', marginRight: '4px' }}
                            //       />
                            //       <span>{value}</span>
                            //     </div>
                              // ))}
                            // </div>
                          )}
                        </td>
                      </tr>
                    );
                  }

                  // Default Case
                  return (
                    <tr key={l.elementIndex}>
                      <td>
                        {(label[l.elementName as keyof typeof label] || l.elementName)?.toUpperCase()}:
                      </td>
                      <td className="center-content">
                        {l?.options?.length !== 0
                          ? CheckboxHandler(l?.options, l?.elementValue, l?.elementName)
                          : l?.elementValue}
                      </td>
                      <td>
                        {(label[column2Item?.elementName as keyof typeof label] || column2Item?.elementName)?.toUpperCase()}
                        {column2Item?.elementName ? ':' : ''}
                      </td>
                      <td className="center-content">
                        {column2Item?.elementValue}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
          <table className="CGR-table border-top-0">
          <th className='padding-4 border-top-0 d-grey-bg'>
          </th>
            <thead>
              <tr>
                {masteringStore.ColorGradingData.dynData.headers?.map((headerName) => (
                  <td
                    className="center-content padding-2  l-grey-bg bold-extra"
                    key={headerName}
                  >
                    {headerName?.toUpperCase()}
                  </td>
                ))}
              </tr>
            </thead>
            <tbody>
              {masteringStore.ColorGradingData.dynData.body?.map((bodyData) => (
                <tr key={bodyData.uuid}>
                  {masteringStore.ColorGradingData.dynData.headers?.map((headerName, index) => (
                    <td
                      className={
                        headerName === 'Defects ' && index === masteringStore.ColorGradingData.dynData.headers.length - 1
                          ? 'border-top-0'
                          : headerName === 'Defects '
                          ? ''
                          : 'center-content'
                      }
                      key={headerName}
                    >
                      {typeof bodyData[headerName] === 'object' ? (
                        JSON.stringify(bodyData[headerName])
                      ) : bodyData[headerName]?.includes('DRP FILE NAME') ? (
                        <span>
                          <span style={{ marginRight: '2px' }}>{CheckboxHandler(
                            initialOptions,
                            YesOrNoResponse(bodyData[headerName]),
                            'DM'
                          )}</span>
                          {extractDrpFileName(bodyData[headerName])}
                        </span>
                      ) : (
                        bodyData[headerName]
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <table className="CGR-table border-top-0">
            <thead></thead>
            <tbody>
              {getColumn(masteringStore.ColorGradingData.localMetaData[1], 1)?.map((l, index) => (
                <tr
                  key={l.elementIndex}
                  // className={
                  //   index === 0 ||
                  //   index === getColumn(localMetaData[1], 1)?.length - 1
                  //     ? 'border-top-0'
                  //     : ''
                  // }
                >
                  <td
                  // className={index === 0 ? 'border-top-0' : ''}
                  >
                    {label[l.elementName as keyof typeof label] === 'Signature with Date' && <><br/><br/><br/><br/></>}
                    {label[l.elementName as keyof typeof label] ||
                      l.elementName}
                    
                  </td>
                  <td
                    // className={
                    //   index === 0
                    //     ? ' center-content border-top-0'
                    //     : 'center-content'
                    // }
                    className={'center-content'}
                  >
                    {l.elementType ===  'DatePicker' && <><br/><br/><br/><br/></> }
                    {l.elementType === 'DatePicker' ? dateConvertHandler(l.elementValue,) : l.elementValue}
                  </td>
                  <td
                  // className={index === 0 ? 'border-top-0' : ''}
                  >
                    {label[getColumn(masteringStore.ColorGradingData.localMetaData[1], 2)?.[index]?.elementName as keyof typeof label] === 'Signature with Date' && <><br/><br/><br/><br/></>}
                    {label[
                      getColumn(masteringStore.ColorGradingData.localMetaData[1], 2)?.[index]
                        ?.elementName as keyof typeof label
                    ] || getColumn(masteringStore.ColorGradingData.localMetaData[1], 2)?.[index]?.elementName}
                    
                  </td>
                  <td
                    // className={
                    //   index === 0
                    //     ? ' center-content border-top-0'
                    //     : 'center-content'
                    // }
                    className={'center-content'}
                  >
                     {(getColumn(masteringStore.ColorGradingData.localMetaData[1], 2)?.[index]?.elementType === 'DatePicker') && <><br/><br/><br/><br/></> }
                     {(getColumn(masteringStore.ColorGradingData.localMetaData[1], 2)?.[index]?.elementType === 'DatePicker') ? dateConvertHandler(getColumn(masteringStore.ColorGradingData.localMetaData[1], 2)?.[index]?.elementValue,'') : getColumn(masteringStore.ColorGradingData.localMetaData[1], 2)?.[index]?.elementValue}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <table className="CGR-table border-top-0">
            <thead>
              <tr>
                <td className="center-content padding-3 border-top-0 bold grey-bg white-text">
                  NFHM QC Supervisor feedback
                </td>
              </tr>
              <tr>
                {masteringStore.ColorGradingData.dynDataOne.headers?.map((headerName) => (
                  <td
                    className="center-content l-grey-bg padding-2 bold"
                    key={headerName}
                  >
                    {headerName}
                  </td>
                ))}
              </tr>
            </thead>
            <tbody>
              {masteringStore.ColorGradingData.dynDataOne.body?.map((bodyData) => (
                <tr key={bodyData.uuid}>
                  {masteringStore.ColorGradingData.dynDataOne.headers?.map((headerName) => (
                    <td
                      className={
                        headerName === 'Defects' ? '' : 'center-content'
                      }
                      key={headerName}
                    >
                      {typeof bodyData[headerName] === 'object'
                        ? JSON.stringify(bodyData[headerName])
                        : bodyData[headerName]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <table className="CGR-table border-top-0">
            <tbody>
              <tr>
                <td className="border-top-0">
                  NFHM GRADING QC Supervisor Comments: {masteringStore.ColorGradingData.summary}
                </td>
                <br />
                <br />
                <br />
                <br />
              </tr>
            </tbody>
          </table>
          <table className="CGR-table border-top-0">
            <tbody>
              <tr className="bold">
                <td className="border-top-0">
                <br /> <br /> <br /> <br /><br/>
                  {label[
                    masteringStore.ColorGradingData.localMetaData[2]?.[0].elementName as keyof typeof label
                  ] || masteringStore.ColorGradingData.localMetaData[2]?.[0].elementName}{' '}
                  : {masteringStore.ColorGradingData.localMetaData[2]?.[0].elementValue}
                </td>
                <td className="border-top-0">
                <br /> <br /> <br /> <br /><br/>
                  {
                    label[
                      masteringStore.ColorGradingData.localMetaData[3]?.[0].elementName as keyof typeof label
                    ]
                  }{' '}
                  : {dateConvertHandler(masteringStore.ColorGradingData.localMetaData[3]?.[0].elementValue)}
                </td>
              </tr>
            </tbody>
          </table>
           {isDualPDF ? 
            <div style={{ flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'flex-end' }}>
          {/* eslint-disable-next-line */}
          <img
            src={getImageSrc('bala')}
            style={{ width: 65, height: 35 }}
          />
        </div> : null}
        </div>
      </body>
    </html>
  );

  const html = ReactDOMServer.renderToStaticMarkup(element);

  return (
    <Document>
      <Page style={{ padding: '18px' }}>
        <Html>{html}</Html>
      </Page>
    </Document>
  );
}

export default observer(ColorGradingReport);
